import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { isEmail, isLength } from 'validator';

import { onAlertStatusError, onHandleStatusError } from './useError';
import { useInput } from './useInput';
import { useGetInfo } from './useGetInfo';

import AuthContext from '../modules/contexts/Auth';
import ModalContext from '../modules/contexts/Modalcontext';

import * as api from '../lib/api/api';
import { ERROR_RETRY, ERROR_VALIDATE, STATUS_MESSAGE } from '../lib/Error';
import { HOME, SIGNIN } from '../lib/routes/path';
import { formatDate } from '../lib/util/Date';
import {
  AllowMarketingInduceModal,
  SIGNUP_INDUCE,
} from '../components/common/organism/AllowMarketing';

const isValidPassword = (value) => {
  return /^(?!((?:[A-Za-z]+)|(?:[~!@#$%^&*()_+=]+)|(?:[0-9]+))$)[A-Za-z\d~!@#$%^&*()_+=]{6,15}$/.test(
    value,
  );
};

export const AUTH_VALIDATE = {
  name: (value) => !isLength(value, { min: 1, max: 100 }),
  email: (value) => !isEmail(value),
  password: (value) => !isValidPassword(value),
  oldPassword: (value) => !isValidPassword(value),
  mobile: (value) => !isLength(value, { min: 10, max: 11 }),
};

export const useSignUp = () => {
  const history = useHistory();

  const {
    actions: { signin },
  } = useContext(AuthContext);

  const validate = {
    ...AUTH_VALIDATE,
    passwordConfirm: (value) => state.password !== value,
  };

  const { state, setValue, onValidate, onChangeValue, inputRef } = useInput({
    initialState: SIGNUP_INITIAL_STATE,
    validate,
    error: ERROR_VALIDATE,
  });
  const [allowMarketing, setAllowMarketing] = useState(false);

  const [isLoading, setIsloading] = useState(false);

  const {
    nameError,
    emailError,
    mobileError,
    passwordError,
    passwordConfirmError,
  } = state;

  const isValidateTrue = [
    nameError,
    emailError,
    mobileError,
    passwordError,
    passwordConfirmError,
  ].every((error) => error === null);

  const allowMarketingCheck = () => {
    setAllowMarketing(!allowMarketing);
  };

  const onSignup = () => {
    const { name, email, mobile, password } = state;
    const business = 'BABYFACE';
    const message = STATUS_MESSAGE.signUp;

    setIsloading(true);
    api
      .babyfaceSignup({
        business,
        email,
        mobile,
        password,
        name,
        allowMarketing,
      })
      .then((response) => {
        alert(message[200]);
        signin({ email, password })
          .then(() => {
            history.push(HOME);
            if (allowMarketing)
              api.postVoucherCoupon({ code: '22_11_MARK_AGR' }).catch(() => {});
          })
          .catch(() => history.push(SIGNIN));
      })
      .catch((error) => {
        setIsSignupFieldCheckCompleted(false);
        const {
          status,
          data: { invalidField },
        } = error.response;
        onHandleStatusError({
          message,
          error,
          errorHandler: () =>
            setValue({
              name: [`${invalidField}Error`],
              value:
                status === 400
                  ? message[status][invalidField]
                  : status === 409 && message[status],
            }),
        });
      })
      .finally(() => setIsloading(false));
  };

  const {
    setIsSignupFieldCheckCompleted,
    onClickSignupButton,
  } = useClickSignupButton({
    onSignup,
    isValidateTrue,
    allowMarketing,
    allowMarketingCheck,
  });

  return {
    state,
    inputRef,
    onClickSignupButton,
    onValidate,
    onChangeValue,
    isLoading,
    isValidateTrue,
    allowMarketing,
    allowMarketingCheck,
  };
};

const SIGNUP_INITIAL_STATE = {
  name: '',
  email: '',
  mobile: '',
  password: '',
  passwordConfirm: '',
};

export const useSnsSignUp = () => {
  const { state, setValue, setValueAll, onChangeValue } = useInput({
    initialState: {
      accountId: 0,
      contactInfo: {
        mobile: '',
      },
      name: '',
      email: '',
      mobile: '',
      isPageLoading: false,
      contract: { allowMarketing: false },
      isCompleted: false,
    },
    validate: AUTH_VALIDATE,
  });

  const { userInfo, isError } = useGetInfo();

  const isMounted = useRef(false);

  useEffect(() => {
    if (isMounted.current) {
      setValueAll({
        object: userInfo,
      });
    } else isMounted.current = true;
  }, [userInfo]);

  const {
    state: { logged },
    actions: { setLogged },
  } = useContext(AuthContext);

  useEffect(() => {
    const message = STATUS_MESSAGE.signUp;

    if (state.isCompleted) {
      alert(message[200]);
      setLogged();
    }
  }, [state.isCompleted]);

  const isValidateTrue = !AUTH_VALIDATE.mobile(state.mobile);

  const putUserInfo = () => {
    const { contract, mobile, name } = state;

    setValue({ name: 'isLoading', value: true });

    api
      .putCurrentEnduserInfo({
        contract,
        contactInfo: { mobile },
        name,
      })
      .then(() => {
        setValue({ name: 'isCompleted', value: true });
        if (contract.allowMarketing)
          api.postVoucherCoupon({ code: '22_11_MARK_AGR' }).catch(() => {});
        else {
          api.getVoucherCouponList({ status: 'usable' }).then((response) => {
            const marketingAgreeCoupon = response.data.find(
              (e) => e.code === '22_11_MARK_AGR',
            );
            if (marketingAgreeCoupon) {
              api.putVoucherCouponStatus({
                id: marketingAgreeCoupon.id,
                status: 'EXPIRED',
              });
            }
          });
        }
      })
      .catch(() => {
        alert(ERROR_RETRY);
        setIsSignupFieldCheckCompleted(false);
      })
      .finally(() => setValue({ name: 'isLoading', value: false }));
  };

  const allowMarketingCheck = () => {
    setValue({
      name: 'contract',
      value: {
        ...state.contract,
        allowMarketing: !state.contract.allowMarketing,
      },
    });
  };

  const {
    setIsSignupFieldCheckCompleted,
    onClickSignupButton,
  } = useClickSignupButton({
    onSignup: putUserInfo,
    isValidateTrue,
    allowMarketing: state.contract.allowMarketing,
    allowMarketingCheck,
  });

  return {
    isError,
    logged,
    onChangeValue,
    state,
    onClickSignupButton,
    isValidateTrue,
    isLoading: state.isLoading,
    allowMarketing: state.contract.allowMarketing,
    allowMarketingCheck,
  };
};

const useClickSignupButton = ({
  onSignup,
  isValidateTrue,
  allowMarketing,
  allowMarketingCheck,
}) => {
  const [
    isSignupFieldCheckCompleted,
    setIsSignupFieldCheckCompleted,
  ] = useState(false);

  useEffect(() => {
    isSignupFieldCheckCompleted && onSignup();
  }, [isSignupFieldCheckCompleted]);

  const { onModal } = useContext(ModalContext).actions;

  const onClickSignupButton = () => {
    if (isValidateTrue) {
      if (allowMarketing) {
        setIsSignupFieldCheckCompleted(true);
      } else {
        onModal({
          title: SIGNUP_INDUCE.title,
          content: (
            <AllowMarketingInduceModal
              allowMarketing={allowMarketing}
              modalText={SIGNUP_INDUCE.text}
              onClick={(setChecked) => {
                setChecked((prevState) => !prevState);
                allowMarketingCheck();
              }}
            />
          ),
          buttonText: '가입하기',
          afterConfirmEvent: () => setIsSignupFieldCheckCompleted(true),
        });
      }
    }
  };
  return {
    setIsSignupFieldCheckCompleted,
    onClickSignupButton,
  };
};

export const useSignIn = () => {
  const {
    state: { logged, isLoading },
    actions,
  } = useContext(AuthContext);

  const { state, onValidate, onChangeValue, setValue, inputRef } = useInput({
    initialState: SIGNIN_INITIAL_STATE,
    validate: AUTH_VALIDATE,
  });

  const onSignin = (e) => {
    const { email, password } = state;
    isValidateTrue &&
      actions.signin({ email, password }).catch((error) => {
        const { cause } = error.response.data;
        const message = STATUS_MESSAGE.login[401];
        const errorField = {
          AUTH_NO_USER: 'email',
          AUTH_PASSWORD_MISSMATCH: 'password',
        };

        Object.keys(message).includes(cause)
          ? setValue({
              name: `${errorField[cause]}Error`,
              value: message[cause],
            })
          : alert(ERROR_RETRY);
      });
  };

  const isValidateTrue =
    state.emailError === null && state.passwordError === null;

  return {
    state,
    inputRef,
    logged,
    isLoading,
    isValidateTrue,
    onValidate,
    onChangeValue,
    onSignin,
  };
};

const SIGNIN_INITIAL_STATE = {
  password: '',
  passwordError: '',
  email: '',
  emailError: '',
};

export const useFindEmail = () => {
  const {
    state: findEmailState,
    setValueAll,
    onValidate: onValidateFindEmail,
    inputRef: findEmailRef,
    onChangeValue: onChangeFindEmailValue,
  } = useInput({
    initialState: FIND_EMAIL_INITIAL_STATE,
    validate: AUTH_VALIDATE,
  });

  const isFindEmailValidateTrue =
    findEmailState.nameError === null && findEmailState.mobileError === null;

  const onFindEmail = () => {
    const { name, mobile } = findEmailState;
    api
      .findEmail({ name, mobile })
      .then((response) => {
        if (response.data.length === 0) {
          alert(STATUS_MESSAGE.user.findInfo[404]);
          return;
        }
        setValueAll({ object: { id: response.data, findId: true } });
      })
      .catch((error) => {
        alert(ERROR_RETRY);
      });
  };

  return {
    findEmailState,
    onFindEmail,
    onChangeFindEmailValue,
    onValidateFindEmail,
    findEmailRef,
    isFindEmailValidateTrue,
  };
};

const FIND_EMAIL_INITIAL_STATE = {
  name: '',
  mobile: '',
  id: [],
  findId: false,
};

export const useFindPassword = () => {
  const {
    state: findPasswordState,
    setValue,
    onValidate: onValidateFindPassword,
    inputRef: FindPasswordRef,
    onChangeValue: onChangeFindPasswordValue,
  } = useInput({
    initialState: FIND_PASSWORD_INITIAL_STATE,
    validate: AUTH_VALIDATE,
  });

  const isFindPasswordValidateTrue =
    findPasswordState.nameError === null &&
    findPasswordState.emailError === null;

  const onFindPassword = () => {
    const { name, email } = findPasswordState;
    const message = STATUS_MESSAGE.user.findInfo;
    api
      .findPassword({ name, email })
      .then((response) => {
        setValue({ name: 'findPassword', value: true });
      })
      .catch((error) => {
        onAlertStatusError({ message, error });
      });
  };

  return {
    findPasswordState,
    onFindPassword,
    onValidateFindPassword,
    FindPasswordRef,
    onChangeFindPasswordValue,
    isFindPasswordValidateTrue,
  };
};

const FIND_PASSWORD_INITIAL_STATE = {
  name: '',
  email: '',
  findPassword: false,
};

export const useWithDrawal = (reasonList) => {
  const {
    state: { isLoadingWithDrawal, detailReason, reason },
    setValue,
    onChange,
  } = useInput({
    initialState: {
      isLoadingWithDrawal: false,
      detailReason: '',
      reason: '',
    },
  });

  const {
    actions: { onModal },
  } = useContext(ModalContext);

  const {
    actions: { signout },
  } = useContext(AuthContext);

  const today = new Date();
  const date = formatDate(today).fulldateWithText;

  const isCheckedEtc = reason !== reasonList[reasonList.length - 1].label;

  const message = STATUS_MESSAGE.user.withDrawal;

  const doWithdrawal = (modalContent) => {
    if ((!isCheckedEtc && !!detailReason) || (isCheckedEtc && !!reason)) {
      setValue({ name: 'isLoadingWithDrawal', value: true });
      api
        .babyfaceWithdrawal({ detailReason, reason })
        .then((res) => {
          onModal({
            title: '베이비페이스 회원 탈퇴 완료',
            content: modalContent,
            afterConfirmEvent: signout,
            afterCloseEvent: signout,
          });
        })
        .catch((error) => {
          onAlertStatusError({ message, error });
          error.response.status === 404 && signout();
        })
        .finally(() => setValue({ name: 'isLoadingWithDrawal', value: false }));
    } else {
      alert('탈퇴 사유를 선택(입력) 해주세요');
    }
  };

  const onCheckReason = (e) => {
    setValue({ name: 'reason', value: e.target.value });
  };

  return {
    isLoadingWithDrawal,
    detailReason,
    reason,
    date,
    message,
    isCheckedEtc,
    doWithdrawal,
    onChange,
    onCheckReason,
  };
};
